import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import styles from './SideBarStyle.module.css';
import rightSidebarStyles from '../../Components/RightSidebar/rightsidebar.module.css';
import BackIcon from '../../Assets/images/back.svg'
import MenuIcon from '../../Assets/images/menu.svg'
import SaleNotePopup from '../../Components/Popups/SaleNotePopup/SaleNotePopup';
import ClosedBooking from './ClosedBooking';
import SaleInfoSidebar from './SaleInfoSidebar';
import BookingDetailsFromSalesSidebar from './BookingDetailsFromSalesSidebar';
import DropdownImageTrigger from '../../Components/DropDown/DropdownImageTrigger';
import { addSaleNote, refundRequest, postBookingStatus, emailBookingInvoice, postMembershipTransactionRefund } from '../../APICalling/APICalling';
import VoidSalePopup from '../../Components/Popups/CancelBookingPopup/VoidSalePopup';
import { useGlobalContext } from '../../Context/GlobalContext';
import { toast } from 'react-toastify';
import { useInvoiceEmailCooldown } from '../../Utils/useInvoiceEmailCooldown';
import { showAppToast } from '../../Utils/appToast';
import { downloadFileWithAuth } from '../../Utils/downloadFileWithAuth';
import logger from '../../Utils/logger';

const SideBarRight = ({active, onClose, id, status, sale, showBookingDetails, onOpenSale, fullScreen = false, initialStep = null,}) => {
    const navigate = useNavigate();
    const { loggedUser, selectedReason, selectedRemark, bookingDetails, loggedOutletID, selectedOutlet } = useGlobalContext();
    const [isSaleNotePopupActive, setIsSaleNotePopupActive] = useState(false);
    const [isVoidSalePopupActive, setIsVoidSalePopupActive] = useState(false);
    const [isAddingNote, setIsAddingNote] = useState(false);
    const [currentStep, setCurrentStep] = useState('info'); // Track current step for header
    const [currentStatus, setCurrentStatus] = useState(status); // Track current status locally
    const [resolvedBookingId, setResolvedBookingId] = useState(null);
    const [salesHeaderOverride, setSalesHeaderOverride] = useState(null);
    // const effectiveBookingId = resolvedBookingId || id;
    const saleId = id;
    const bookingId = resolvedBookingId;


    // Update local status when prop changes (e.g., when opening a different sale)
    useEffect(() => {
        setCurrentStatus(status);
        setResolvedBookingId(null);
        setSalesHeaderOverride(null);
    }, [status, id]);

    useEffect(() => {
        if (!active) {
            setCurrentStep('info');
        }
    }, [active]);
    
    // Add sale note state
    const [saleNote, setSaleNote] = useState('');
    const invoiceBaseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
    const [customerEmail, setCustomerEmail] = useState(bookingDetails?.user?.email || sale?.email || salesHeaderOverride?.customerEmail || '');
    const hasCustomerEmail = Boolean(
        String(customerEmail || '').trim()
    );
    const {
        startCooldown: startInvoiceEmailCooldown,
        getRemaining: getInvoiceEmailCooldownRemaining,
    } = useInvoiceEmailCooldown();
    const invoiceEmailCooldownRemaining = getInvoiceEmailCooldownRemaining(bookingId);
    const isInvoiceEmailCoolingDown = invoiceEmailCooldownRemaining > 0;
    const emailInvoiceTooltip = isInvoiceEmailCoolingDown
        ? 'Invoice emailed. Please wait before sending again.'
        : (!hasCustomerEmail ? 'Customer has no email address.' : '');

    useEffect(() => {
        setCustomerEmail(bookingDetails?.user?.email || sale?.email || salesHeaderOverride?.customerEmail || '');
    }, [bookingDetails?.user?.email, sale?.email, salesHeaderOverride?.customerEmail, id]);

    useEffect(() => {
        const handleCustomerUpdated = (event) => {
            const updatedCustomer = event?.detail?.customer;
            const currentCustomerId = bookingDetails?.user?.id;
            if (!updatedCustomer || !currentCustomerId) {
                return;
            }
            if (String(updatedCustomer.id) !== String(currentCustomerId)) {
                return;
            }
            setCustomerEmail(updatedCustomer.email || '');
        };

        window.addEventListener('customerUpdated', handleCustomerUpdated);
        return () => window.removeEventListener('customerUpdated', handleCustomerUpdated);
    }, [bookingDetails?.user?.id]);

    const normalizeStatusKey = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '');
    const statusMatches = (value, statuses) => statuses.includes(normalizeStatusKey(value));
    const checkoutStatuses = ['pending', 'confirmed', 'holding', 'partial', 'open', 'open(inprogress)','open(in progress)', 'check-in(inprogress)', 'checkin(inprogress)'];
    const closedStatuses = ['paid', 'completed', 'compleded', 'closed', 'cancelled', 'canceled'];

    const computeHeaderTitle = () => {
        const isMembership = String(sale?.type || '').toLowerCase() === 'membership';
        const headerSaleId = isMembership ? (sale?.sale_id ?? id) : id;

        // Check current step first
        if (currentStep === 'payment') return 'Payment';
        if (currentStep === 'confirmation') return 'Payment Confirmation';
        const raw = String(currentStatus || '').trim().toLowerCase();
        const titleOverride = String(salesHeaderOverride?.title || '').trim().toLowerCase();
        if (raw === 'voided' || raw === 'refunded' || titleOverride.startsWith('membership refund')) {
            return `Refund #${headerSaleId}`;
        }
        if (statusMatches(raw, closedStatuses)) return `Sale #${headerSaleId} (closed)`;

        // Membership top-up partial/open sales should read like the other
        // partial sale flows instead of showing the resolved sale title.
        if (isMembership) {
            return 'Checkout';
        }

        if (salesHeaderOverride?.title && (currentStep === 'info' || currentStep === 'saleinfo')) {
            if (isMembership) {
                return salesHeaderOverride.title.replace(/^Top-up\b/i, 'Sale');
            }
            return salesHeaderOverride.title;
        }

        // For other sales: show checkout if in checkout statuses
        if (statusMatches(raw, checkoutStatuses)) return 'Checkout';
        return raw ? `Sale #${headerSaleId} (${raw})` : `Sale #${headerSaleId}`;
    };

    const openSaleNotePopup = () => {
        setIsSaleNotePopupActive(true);
    }
    const closeSaleNotePopup = () => {
        setIsSaleNotePopupActive(false);
    }

    const handleAddNote = async (noteText) => {
        setIsAddingNote(true);
        try {
            const params = {
                id: saleId,
                panel: "outlet",
                note: noteText.trim(),
                updated_by: loggedUser
            };

            const response = await addSaleNote(params);
            if (response?.data?.success) {
                toast.success('Note added successfully!');
                setIsSaleNotePopupActive(false);
                // Trigger refresh in BookingDetailsFromSalesSidebar
                try { 
                    window.dispatchEvent(new CustomEvent('salesNoteAdded')); 
                } catch {}
            } else {
                toast.error('Failed to add note. Please try again.');
            }
        } catch (error) {
            logger.error('Error adding note:', error);
            toast.error('Error adding note. Please try again.');
        } finally {
            setIsAddingNote(false);
        }
    };

    const handleVoid = () => {
        setIsVoidSalePopupActive(true);
    };

    const handleDiscard = () => {
        // Just close the sidebar for discard
        onClose();
    };

    const handleOpenRelatedSale = (saleId) => {
        const relatedSaleId = String(saleId || '').replace('#', '').trim();
        if (!relatedSaleId || typeof onOpenSale !== 'function') return;

        onClose();
        window.setTimeout(() => {
            navigate(`/sales?search=${encodeURIComponent(relatedSaleId)}`);
            onOpenSale({
                id: relatedSaleId,
                sale_id: relatedSaleId,
                type: sale?.type || 'booking',
                status: '',
            });
        }, 0);
    };

    const openInvoiceDocument = async (format) => {
        if (!bookingId) {
            toast.error('Sale ID not found.');
            return;
        }

        if (!invoiceBaseUrl) {
            toast.error('Invoice base URL is not configured.');
            return;
        }
        let id = '';
        if (sale?.type === 'membership') {
            id = saleId;
        } else {
            id = bookingDetails?.sale_id ?? sale?.sale_id ?? bookingId;
        }

        try {
            await downloadFileWithAuth(
                `${invoiceBaseUrl}/sales/${id}/invoice/${format}`,
                `invoice.${format}`
            );
        } catch (error) {
            logger.error('Invoice download failed:', error);
            toast.error(error?.message || 'Failed to download invoice');
        }
    };

    const handleEmailInvoice = async () => {
        if (!bookingId) {
            showAppToast('error', 'Sale ID not found.');
            return;
        }

        try {
            const response = await emailBookingInvoice(bookingId);
            const payload = response?.response?.data ?? response?.data ?? response ?? {};
            const nestedPayload = payload?.data && typeof payload.data === 'object' ? payload.data : {};
            const isExplicitFailure = [payload?.success,nestedPayload?.success,payload?.status,nestedPayload?.status,
            ].some((value) => value === false || value === 'false' || value === 0 || value === '0');
            const isSuccess =
                payload?.success === true ||
                nestedPayload?.success === true ||
                payload?.status === true ||nestedPayload?.status === true ||
                (
                    payload?.success !== false &&
                    nestedPayload?.success !== false &&
                    payload?.status !== false && nestedPayload?.status !== false &&
                    !payload?.errors &&
                    !nestedPayload?.errors
                );
            const successMessage = payload?.message || nestedPayload?.message || 'Invoice emailed successfully.';
            const errorMessage = payload?.message || nestedPayload?.message || 'Failed to email invoice.';
            const isMissingEmail = errorMessage === 'Customer has no email address.';

            if (!isExplicitFailure && isSuccess) {
                showAppToast('success', successMessage);
                startInvoiceEmailCooldown(bookingId);
                return;
            }

            if (isMissingEmail) {
                showAppToast('info', errorMessage);
                return;
            }

            showAppToast('error', errorMessage);
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to email invoice.';

            if (errorMessage === 'Customer has no email address.') {
                showAppToast('info', errorMessage);
                return;
            }

            showAppToast('error', errorMessage);
        }
    };

    const closeVoidSalePopup = () => {
        setIsVoidSalePopupActive(false);
    };

    const handleVoidSaleWithRefund = async (refundOptions = {}) => {
        const refundRemark = String(refundOptions?.remark || '').trim();
        const membershipRefund = refundOptions?.membership_refund || null;

        const buildMembershipGiftStatus = (gifts = []) =>
            gifts.reduce((acc, gift) => {
                const giftStatusKey =
                    gift?.id;
                const returnStatus = gift?.return_status || 'returned';
                if (giftStatusKey == null || giftStatusKey === '') {
                    return acc;
                }
                if (returnStatus === 'did_not_issue') {
                    return acc;
                }
                acc[String(giftStatusKey)] = returnStatus;
                return acc;
            }, {});

        if (sale?.type === 'membership' && membershipRefund) {
            const membershipDetail = salesHeaderOverride?.membershipDetail || {};
            const membershipTransactionId =
                membershipDetail?.id ??
                membershipDetail?.membership_transaction_id ??
                sale?.membership_transaction_id ??
                sale?.transaction_id ??
                null;

            if (!membershipTransactionId) {
                return {
                    success: false,
                    message: 'Membership transaction ID not found. Please try again.',
                };
            }

            const membershipRefundPayload = {
                amount: Number(membershipRefund.refund_amount || 0),
                remarks: refundRemark || selectedRemark || selectedReason,
                outlet_id:
                    membershipDetail?.outlet_id ??
                    membershipDetail?.registration_outlet_id ??
                    loggedOutletID ??
                    selectedOutlet?.id ??
                    selectedOutlet,
                panel: 'outlet',
                gift_status: buildMembershipGiftStatus(membershipRefund.gifts),
            };

            try {
                const response = await postMembershipTransactionRefund(
                    membershipTransactionId,
                    membershipRefundPayload
                );
                const success = response?.success || response?.data?.success;

                if (success) {
                    const refundSuccessMessage =
                        response?.message ||
                        response?.data?.message ||
                        'Membership top-up refunded successfully!';
                    setIsVoidSalePopupActive(false);
                    setCurrentStatus('closed');
                    toast.success(refundSuccessMessage);
                    window.dispatchEvent(new CustomEvent('voidSaleCompleted', {
                        detail: {
                            saleId,
                            bookingId,
                            type: 'membership',
                        },
                    }));
                    return;
                }

                return {
                    success: false,
                    message: response?.message || response?.data?.message || 'Failed to process refund. Please try again.',
                };
            } catch (error) {
                logger.error('Error submitting membership refund request:', error);
                return {
                    success: false,
                    message:
                        error?.response?.data?.message ||
                        error?.message ||
                        'Error refunding membership top-up. Please try again.',
                };
            }
        }
  
        const item = {
            booking: sale?.id ?? bookingDetails?.sale_id,
            remark: refundRemark || selectedRemark,
            reason: selectedReason,
            clawback_commission: Boolean(refundOptions.clawback_commission),
            booking_item_ids: Array.isArray(refundOptions.booking_item_ids) ? refundOptions.booking_item_ids : [],
            panel: 'outlet',
            updated_by: loggedUser,
        };
        
        
        try {
            const response = await refundRequest(item);
            const isRefundSuccess = response?.success || response?.data?.success;

            if (isRefundSuccess) {
                const refundSuccessMessage = response?.message || response?.data?.message || 'Sale voided with refund successfully!';
                const saleType = String(sale?.type || '').toLowerCase();
                const skipBookingStatus = saleType === 'product' || saleType === 'voucher';

                if (!skipBookingStatus) {
                    const statusData = {
                        "company": 1,
                        "id": bookingId,
                        "status": "Voided",
                        "panel": "outlet",
                        "updated_by": loggedUser,
                        isVoided: 1,
                    };

                    const statusResponse = await postBookingStatus(statusData);
                    const isStatusSuccess = statusResponse?.success || statusResponse?.data?.success;
                    if (!isStatusSuccess) {
                        logger.error("Status update failed:", statusResponse);
                        return {
                            success: false,
                            message: statusResponse?.message || statusResponse?.data?.message || 'Failed to update sale status. Please try again.',
                        };
                    }
                }

                setIsVoidSalePopupActive(false);
                setCurrentStatus('refunded');
                toast.success(refundSuccessMessage);
                try {
                    window.dispatchEvent(new CustomEvent('voidSaleCompleted'));
                    logger.log('Event dispatched successfully');
                } catch (error) {
                    logger.error('Error dispatching refresh event:', error);
                }
                return;
            }
            logger.error("Refund failed:", response);
            return {
                success: false,
                message: response?.message || response?.data?.message || 'Failed to process refund. Please try again.',
            };
        } catch (error) {
            logger.error("Error submitting refund request:", error);
            return {
                success: false,
                message: error?.response?.data?.message || error?.message || 'Error voiding sale with refund. Please try again.',
            };
        }

    };

    // const handleVoidSaleWithoutRefund = async () => {
        
    //     try {
    //         // Update booking status to refunded without refund (same as RightSidebarWrapper.js handleStatusChange)
    //         const statusData = {
    //             company: 1,
    //             id: bookingId,
    //             status: "refunded",
    //             panel: "outlet",
    //             updated_by: loggedUser,
    //         };

    //         const response = await postBookingStatus(statusData);
    //         if (response?.data?.success) {
                
    //             setIsVoidSalePopupActive(false);
    //             setCurrentStatus('refunded'); // Update local status immediately
    //             toast.success('Sale voided successfully!');
                
    //             // Trigger refresh by dispatching custom event (keep sidebar open)
    //             try {
    //                 window.dispatchEvent(new CustomEvent('voidSaleCompleted'));
    //                 logger.log('Event dispatched successfully');
    //             } catch (error) {
    //                 logger.error('Error dispatching refresh event:', error);
    //             }
    //         } else {
    //             logger.error("Status update failed:", response);
    //             toast.error(response?.data?.message || 'Failed to refund sale. Please try again.');
    //             setIsVoidSalePopupActive(false);
    //             // Keep sidebar open to show error, don't close it
    //         }
    //     } catch (error) {
    //         logger.error('Error refunding sale:', error);
    //         toast.error('Error refunding sale. Please try again.');
    //         setIsVoidSalePopupActive(false);
    //         onClose();
    //     }
        
    // };
    const statusLabel = String(currentStatus || '').toLowerCase();
    const membershipDetail = salesHeaderOverride?.membershipDetail || {};
    const membershipActionLabel = String(membershipDetail?.action_label || '').trim().toLowerCase();
    const membershipAction = String(membershipDetail?.action || '').trim().toLowerCase();
    const membershipHeaderTitle = String(salesHeaderOverride?.title || '').trim().toLowerCase();
    const isMembershipRefundSale =
        sale?.type === 'membership' &&
        (
            statusLabel === 'refunded' ||
            membershipHeaderTitle.startsWith('membership refund') ||
            membershipHeaderTitle.startsWith('refund #') ||
            membershipActionLabel.includes('refund') ||
            membershipAction === 'refund' ||
            membershipAction === 'refund_membership'
        );

    const getDropdownOptions = () => {
        // logger.log("currentStatus", currentStatus);
        
        const membershipFirst =
            sale?.type === 'membership' ? [
                // { label: 'Discard', action: handleDiscard }
            ] : [];
        const hasChildRefundSale = Boolean(bookingDetails?.child_sale_id || sale?.child_sale_id || salesHeaderOverride?.childSaleId);
        const refundSaleOption = {
            label: 'Refund sale',
            action: handleVoid,
            disabled: hasChildRefundSale,
            tooltip: hasChildRefundSale ? 'Already refunded' : '',
        };

        if (statusMatches(statusLabel, ['paid', 'completed', 'compleded', 'closed'])) {
            return [
                ...membershipFirst,
                { label: saleNote ? 'Edit Notes' : 'Add Notes', action: openSaleNotePopup },
                ...(isMembershipRefundSale ? [] : [refundSaleOption]),
                { label: 'Download PDF Invoice', action: () => openInvoiceDocument('pdf') },
                // { label: 'Download Doc Invoice', action: () => openInvoiceDocument('doc') },
                // {
                //     label: isInvoiceEmailCoolingDown ? `Email Invoice (${invoiceEmailCooldownRemaining}s)` : 'Email Invoice',
                //     action: handleEmailInvoice,
                //     disabled: !hasCustomerEmail || isInvoiceEmailCoolingDown,
                //     tooltip: emailInvoiceTooltip,
                // },
            ];
        } else if (statusMatches(statusLabel, ['partial', 'open(inprogress)'])) {
            return [
                ...membershipFirst,
                { label: saleNote ? 'Edit Notes' : 'Add Notes', action: openSaleNotePopup },
                // { label: 'Refund', action: handleVoid },
            ];
        } else if (statusMatches(statusLabel, ['pending', 'confirmed', 'open', 'check-in(inprogress)', 'checkin(inprogress)'])) {
            if (sale?.type === 'membership') {
                return membershipFirst;
            }
            return [
                // { label: 'Discard', action: handleDiscard }
            ];
        } else if (statusLabel === 'refunded') {
            return [
                ...membershipFirst,
                { label: saleNote ? 'Edit Notes' : 'Add Notes', action: openSaleNotePopup },
            ];
        } else if (statusLabel === 'holding') {
            return [
                ...membershipFirst,
                { label: saleNote ? 'Edit Notes' : 'Add Notes', action: openSaleNotePopup },
                ...(sale?.type === 'membership' ? [] : [
                    // { label: 'Discard', action: handleDiscard }
                ]),
            ];
        }

        // Default fallback
        return [
            ...membershipFirst,
            { label: saleNote ? 'Edit Notes' : 'Add Notes', action: openSaleNotePopup },
        ];
    };
  
  return (
    <>
    <div className={`${styles.sideBar} ${styles.right} ${active ? styles.active : ""}`} style={fullScreen ? { top:0, maxHeight: '100%' } : { maxHeight: '100%' }}>
       {currentStep !== 'checkout' && currentStep !== 'payment' && currentStep !== 'confirmation' &&  <div className={styles.sideBarHeader}>
            <button className={styles.backButton} onClick={onClose}>
                <img src={BackIcon} alt="Back" />
            </button>
            <h2>
                {computeHeaderTitle()}
            </h2>       
            <div className="ml-auto">
                <div className={rightSidebarStyles.menuIcon}>
                    <DropdownImageTrigger
                        iconSrc={MenuIcon}
                        options={getDropdownOptions()}
                        menuClass={rightSidebarStyles.customMenuClass}
                    />
                </div>
            </div>
        </div>}
            <div className={styles.sideBarContent}>
                <BookingDetailsFromSalesSidebar 
                    bookingId={id} 
                    bookingType={sale?.type}
                    initialStep={initialStep || sale?.initialStep || null}
                    onStepChange={setCurrentStep}
                    onSaleNoteChange={setSaleNote}
                    onBookingIdResolved={setResolvedBookingId}
                    active={active}
                    onShowVoidSalePopup={handleVoid}
                    onCloseSidebar={onClose}
                    onSalesSidebarMeta={setSalesHeaderOverride}
                    onSaleStatusResolved={setCurrentStatus}
                    onOpenRelatedSale={handleOpenRelatedSale}
                />
            </div>
    </div>
    {isSaleNotePopupActive && (
        <SaleNotePopup
            onClose={closeSaleNotePopup}
            addSaleNote={handleAddNote}
            editableNote={saleNote}
        />
    )}
    {isVoidSalePopupActive && (
        <VoidSalePopup
            onClose={closeVoidSalePopup}
            onConfirm={() => {}}
            bookingDataFromAPI={{
                ...(salesHeaderOverride?.membershipDetail || {}),
                booking_id: bookingId,
                sale_id: saleId,
                type: sale?.type || 'booking',
            }}
            voidedSaleWithRefund={handleVoidSaleWithRefund}
            // voidSaleWithoutRefund={handleVoidSaleWithoutRefund}
            from="salePage"
        />
    )}
    </>
  )
}

export default SideBarRight
